import React, { useState, useRef, useEffect } from "react";
import * as Shared from "./Shared.js";

/*
 * File-view of the section outline: folder/leaf icon, outline number, current
 * v{major}.{minor}, state dot, "needs review" flag, and a kebab (move/add/delete).
 * Moves and deletes only affect the current draft version.
 */

const SectionTree = ({
    sections,
    svByIdentifier = {},
    numbers,
    activeSectionId,
    onSelect,
    canEdit,
    onMove,          // (section, direction: -1 | 1)
    onAddSection,    // (parentId | null)
    onDeleteSection, // (section)
    onApplyTemplate, // () -> opens template picker
}) => {
    const [openKebab, setOpenKebab] = useState(null);
    const [stateFilter, setStateFilter] = useState("all");   // "all" | SectionVersion state (0 Draft, 1 Approved, 2 Rejected)
    const rootRef = useRef(null);

    useEffect(() => {
        const close = (e) => {
            if (rootRef.current && !rootRef.current.contains(e.target)) setOpenKebab(null);
        };
        document.addEventListener("mousedown", close);
        return () => document.removeEventListener("mousedown", close);
    }, []);

    const byParent = {};
    (sections || []).forEach((s) => {
        const p = s.ParentId ?? "root";
        (byParent[p] = byParent[p] || []).push(s);
    });
    Object.values(byParent).forEach((list) =>
        list.sort((a, b) => (a.Order ?? 0) - (b.Order ?? 0))
    );

    // Filtered view: matching sections plus their ancestors (dimmed) so the hierarchy stays readable.
    const stateOf = (s) => svByIdentifier[s.SectionIdentifier]?.State ?? 0;
    const isFiltering = stateFilter !== "all";
    const matches = new Set();
    const visible = new Set();
    if (isFiltering) {
        const byId = {};
        (sections || []).forEach((s) => { byId[s.Id] = s; });
        (sections || []).filter((s) => stateOf(s) === Number(stateFilter)).forEach((s) => {
            matches.add(s.Id);
            for (let cur = s; cur; cur = cur.ParentId != null ? byId[cur.ParentId] : null) visible.add(cur.Id);
        });
    }

    const renderNodes = (parentKey, depth) =>
        (byParent[parentKey] || []).map((s, idx, arr) => {
            if (isFiltering && !visible.has(s.Id)) return null;
            const isActive = s.Id == activeSectionId;
            const kebabOpen = openKebab === s.Id;
            const hasKids = !!byParent[s.Id]?.length;
            const sv = svByIdentifier[s.SectionIdentifier];
            return (
                <React.Fragment key={s.Id}>
                    <div
                        className={`td-tnode ${isActive ? "active" : ""}`}
                        style={{ ...(kebabOpen ? { background: "#f7fafc" } : {}), ...(isFiltering && !matches.has(s.Id) ? { opacity: 0.5 } : {}) }}
                        onClick={() => onSelect(s)}
                    >
                        <span className="num" style={{ paddingLeft: depth * 12 }}>
                            <i className={hasKids ? "far fa-folder" : "far fa-file-alt"} style={{ marginRight: 6, color: "#94a3b8" }} />
                            {numbers[s.Id]}
                        </span>
                        <span className="ttl" title={s.Title}>{s.Title || "Untitled section"}</span>
                        {sv && <span className="td-vmini">v{sv.Major}.{sv.Minor}</span>}
                        {s.NeedsReview && <i className="fas fa-exclamation-circle" title="A linked section changed — review this one" style={{ color: "#f7b204", marginRight: 4 }} />}
                        <Shared.SectionVersionDot state={sv?.State ?? 0} />
                        {canEdit && (
                            <span
                                className="td-tnode-kebab"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setOpenKebab(kebabOpen ? null : s.Id);
                                }}
                            >
                                <i className="fas fa-ellipsis-v" />
                            </span>
                        )}
                        {kebabOpen && (
                            <div className="td-kebab-menu" onClick={(e) => e.stopPropagation()}>
                                <div
                                    className={`td-kebab-menu-item ${idx === 0 ? "disabled" : ""}`}
                                    onClick={() => { setOpenKebab(null); onMove(s, -1); }}
                                >
                                    <i className="fas fa-arrow-up" /> Move up
                                </div>
                                <div
                                    className={`td-kebab-menu-item ${idx === arr.length - 1 ? "disabled" : ""}`}
                                    onClick={() => { setOpenKebab(null); onMove(s, 1); }}
                                >
                                    <i className="fas fa-arrow-down" /> Move down
                                </div>
                                <div
                                    className="td-kebab-menu-item"
                                    onClick={() => { setOpenKebab(null); onAddSection(s.Id); }}
                                >
                                    <i className="fas fa-plus" /> Add subsection
                                </div>
                                <div
                                    className="td-kebab-menu-item danger"
                                    onClick={() => { setOpenKebab(null); onDeleteSection(s); }}
                                >
                                    <i className="far fa-trash-alt" /> Delete section
                                </div>
                            </div>
                        )}
                    </div>
                    {renderNodes(s.Id, depth + 1)}
                </React.Fragment>
            );
        });

    return (
        <div className="td-tree-panel" ref={rootRef}>
            <div className="td-tree-headrow">
                <div className="td-row" style={{ gap: 8 }}>
                    <span className="td-tree-headrow-title">Structure</span>
                    <select className="td-tree-filter" value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} title="Filter sections by state">
                        <option value="all">All</option>
                        <option value="0">Drafts</option>
                        <option value="2">Rejected</option>
                        <option value="1">Approved</option>
                    </select>
                </div>
                {canEdit && (
                    <div className="td-tree-head-actions">
                        {onApplyTemplate && (
                            <i
                                className="fas fa-layer-group"
                                title="Apply a template"
                                onClick={onApplyTemplate}
                            />
                        )}
                        <i
                            className="fas fa-plus-circle add"
                            title="Add top-level section"
                            onClick={() => onAddSection(null)}
                        />
                    </div>
                )}
            </div>

            <div className="td-tree-body">
                {(sections || []).length === 0 ? (
                    <div className="td-tree-hint">
                        No sections yet. {canEdit ? "Add one with the + above, or apply a template." : ""}
                    </div>
                ) : isFiltering && matches.size === 0 ? (
                    <div className="td-tree-hint">No sections in this state.</div>
                ) : (
                    renderNodes("root", 0)
                )}
            </div>

            {(sections || []).length > 0 && (
                <div className="td-tree-hint">
                    <div>Select a section from the tree to view or edit it, or add a new one.</div>
                    {canEdit && (
                        <div style={{ marginTop: 4 }}>
                            Moving or deleting a section changes this draft version only —
                            it isn&apos;t applied to the last approved version until this
                            version is approved.
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SectionTree;
